import React, { Key, useEffect, useMemo, useState } from 'react';
import { useRequest } from 'ahooks';
import styles from './index.module.scss';
import { Empty, Input, Select, Tooltip, Tree } from '@maxtropy/components';
import { Divider, Spin } from 'antd';
import {
  apiV2ProductionCostGetEnergyUnitGroupListPost,
  apiV2ProductionCostGetGroupEnergyUnitListPost,
  V2ProductionCostGetGroupEnergyUnitListPostResponse,
} from '@maxtropy/device-customer-apis-v2';
import { GlicoUnitComsumptionContext } from '../..';
import { DataNode } from 'antd/es/tree';
import { LockFilled } from '@ant-design/icons';
import { findFirstDisabledPath } from '../../utils';

const { Search } = Input;

const EnergyTree = () => {
  const { setUnitId, groupId, setGroupId } = React.useContext(GlicoUnitComsumptionContext);
  const [searchValue, setSearchValue] = useState<string>(''); // 搜索输入框
  const [autoExpandParent, setAutoExpandParent] = useState(true); // 左侧树自动全部展开
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]); // 左侧树展开的节点
  const [selectedKeys, setSelectedKeys] = useState<Key[]>([]); // 选中的节点
  const [dataList, setDataList] = useState<{ key: React.Key; title: string }[]>([]); // 树形节点平铺列表

  // 获取用能分析组
  const { data: groupOptions } = useRequest(() =>
    apiV2ProductionCostGetEnergyUnitGroupListPost({}).then(res => {
      return (res.list ?? []).map(item => ({ label: item.name as string, value: item.id as number }));
    })
  );

  // 默认选择第一个用能分析组
  useEffect(() => {
    if ((groupOptions ?? []).length > 0) {
      setUnitId?.(undefined);
      setGroupId?.(groupOptions?.[0].value!);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupOptions]);

  // 获取用能单元树形结构
  const { data: unitTreeData, loading } = useRequest(
    () =>
      apiV2ProductionCostGetGroupEnergyUnitListPost({
        energyUnitGroupId: String(groupId),
      }).then(res => {
        setSearchValue('');
        generateList((res.list ?? []) as V2ProductionCostGetGroupEnergyUnitListPostResponse['list']);
        return res.list ?? [];
      }),
    {
      refreshDeps: [groupId],
      ready: !!groupId,
    }
  );

  // 用能单元搜索框
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    if (value) {
      const newExpandedKeys = dataList
        .map(item => {
          if (item.title.indexOf(value) > -1) {
            return getParentKey(item.key, unitTreeData);
          }
          return null;
        })
        .filter((item, i, self) => item && self.indexOf(item) === i);
      setExpandedKeys([...expandedKeys, ...(newExpandedKeys as React.Key[])]);
    }
    setSearchValue(value);
    setAutoExpandParent(true);
  };

  // 获取父节点key
  const getParentKey = (
    key: React.Key,
    tree: V2ProductionCostGetGroupEnergyUnitListPostResponse['list']
  ): React.Key => {
    let parentKey: React.Key;
    for (let i = 0; i < (tree ?? []).length; i++) {
      const node = tree?.[i];
      if (node?.children) {
        if (node.children.some(item => item.energyUnitId === key)) {
          parentKey = node?.energyUnitId ?? '';
        } else if (getParentKey(key, node.children)) {
          parentKey = getParentKey(key, node.children);
        }
      }
    }
    return parentKey!;
  };

  // 生成节点映射表（树形结构-->平铺）
  const generateList = (data: V2ProductionCostGetGroupEnergyUnitListPostResponse['list']) => {
    let tempDataList: { key: React.Key; title: string }[] = [];
    const loopData = (data: V2ProductionCostGetGroupEnergyUnitListPostResponse['list']) => {
      for (let i = 0; i < (data ?? []).length; i++) {
        const node = data?.[i];
        tempDataList.push({ key: node?.energyUnitId ?? '', title: node?.name ?? '' });
        if (node?.children) {
          loopData(node.children);
        }
      }
    };
    loopData(data);
    setDataList(tempDataList);
  };

  // 初始化展开的节点列表
  const initExpandedKeys = (data: DataNode[]) => {
    let keys: number[] = [];
    const loop = (nodeList: DataNode[], level: number = 0) => {
      for (const item of nodeList ?? []) {
        keys.push(item.key as number);
        if (item.children) {
          loop(item.children, level + 1);
        }
      }
    };
    loop(data);
    return keys;
  };

  // 展开节点
  const onExpand = (newExpandedKeys: React.Key[]) => {
    setExpandedKeys([...newExpandedKeys]);
    setAutoExpandParent(false);
  };

  // tree数据生成
  const treeData = useMemo(() => {
    const loop = (data: V2ProductionCostGetGroupEnergyUnitListPostResponse['list']): DataNode[] =>
      (data ?? [])
        .map(item => {
          const strTitle = item.name as string;
          const index = strTitle.indexOf(searchValue);
          const beforeStr = strTitle.substring(0, index);
          const afterStr = strTitle.slice(index + searchValue.length);
          const disableIcon = <LockFilled style={{ color: 'var(--mx-warning-color)', fontSize: 12, marginLeft: 4 }} />;
          let title = <span>{strTitle}</span>;
          if (index > -1) {
            if (item.disabled) {
              title = (
                <Tooltip title="无数据权限" key={item.energyUnitId}>
                  <span>
                    {beforeStr}
                    <span className={styles.highLight}>{searchValue}</span>
                    {afterStr}
                    {disableIcon}
                  </span>
                </Tooltip>
              );
            } else {
              title = (
                <span>
                  {beforeStr}
                  <span className={styles.highLight}>{searchValue}</span>
                  {afterStr}
                </span>
              );
            }
          } else {
            if (item.disabled) {
              title = (
                <Tooltip title="无数据权限" key={item.energyUnitId}>
                  <span>
                    {strTitle}
                    {disableIcon}
                  </span>
                </Tooltip>
              );
            } else {
              title = <span>{strTitle}</span>;
            }
          }

          if (item.children && item.children.length > 0) {
            const children = loop(item.children);
            return {
              title: index > -1 || children.length ? title : null,
              key: item.energyUnitId ?? '',
              disabled: item.disabled,
              children: children.length ? children : undefined,
            };
          }

          return index > -1
            ? {
                key: item.energyUnitId ?? '',
                disabled: item.disabled,
                title,
              }
            : {
                key: item.energyUnitId ?? '',
                disabled: item.disabled,
                title: null,
              };
        })
        .filter(item => !!item.title);
    return loop(unitTreeData);
  }, [searchValue, unitTreeData]);

  // 默认选择分析组的第一个可选的用能单元
  useEffect(() => {
    if (!unitTreeData) return;
    const selectKey = findFirstDisabledPath(unitTreeData);
    if (selectKey) {
      setSelectedKeys([selectKey]);
      setExpandedKeys([...expandedKeys, selectKey]);
      setUnitId?.(selectKey);
    } else {
      setSelectedKeys([]);
    }
  }, [unitTreeData]);

  // 设置展开节点
  useEffect(() => {
    const defaultExpandedKeys = initExpandedKeys(treeData);
    setExpandedKeys(defaultExpandedKeys);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treeData]);

  return (
    <div className={styles.leftSider}>
      <div className={styles.filterArea}>
        <Select
          placeholder="请选择"
          className={styles.selectBox}
          value={groupId}
          allowClear={false}
          options={groupOptions}
          onChange={val => {
            setUnitId?.(undefined);
            setSelectedKeys([]);
            setExpandedKeys([]);
            setDataList([]);
            setGroupId?.(val);
          }}
          showSearch
          optionFilterProp="label"
        />
      </div>
      <div className={styles.searchArea}>
        <Search placeholder="请输入用能单元名称" value={searchValue} allowClear onChange={onChange} />
      </div>
      <div className={styles.splitLine}>
        <Divider className={styles.divider} />
      </div>
      <div className={styles.treeArea}>
        <Spin spinning={loading}>
          {unitTreeData && unitTreeData.length > 0 ? (
            <>
              <div className={styles.tree_box}>
                <Tree
                  className={styles.treeStyle}
                  showIcon
                  blockNode
                  treeData={treeData}
                  selectedKeys={selectedKeys}
                  onExpand={onExpand}
                  expandedKeys={expandedKeys}
                  autoExpandParent={autoExpandParent}
                  onSelect={(selectedKeys, info) => {
                    if (selectedKeys.length === 0) return;
                    setSelectedKeys(selectedKeys);
                    setUnitId?.(Number(selectedKeys[0]));
                  }}
                />
              </div>
            </>
          ) : (
            <Empty style={{ paddingTop: 100 }} />
          )}
        </Spin>
      </div>
    </div>
  );
};

export default EnergyTree;
